import type { LucideIcon } from "lucide-react";
import {
  Armchair,
  Bath,
  BedDouble,
  Boxes,
  CarFront,
  ChevronRight,
  DoorOpen,
  Edit3,
  House,
  Trash2,
  Trees,
  Utensils,
  Warehouse,
  WashingMachine
} from "lucide-react";
import { BlobImage } from "./BlobImage";
import { getRoomInspectionStatus } from "../services/app/reportStatus";
import type { Issue, Room } from "../services/db/schema";

interface RoomCardProps {
  room: Room;
  issues: Issue[];
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
}

export function RoomCard({ room, issues, onOpen, onRename, onDelete }: RoomCardProps): JSX.Element {
  const status = getRoomInspectionStatus(issues);
  const RoomIcon = getRoomIcon(room.name);

  return (
    <article className={`room-card room-card--${status.tone}`}>
      <button
        className="room-card__main"
        type="button"
        onClick={onOpen}
        aria-label={`Open ${room.name}. ${status.label}. ${status.issueLabel}.`}
      >
        <span className="room-card__top">
          <span className="room-card__icon" aria-hidden="true">
            {room.optionalCoverThumbBlobKey ? (
              <BlobImage blobKey={room.optionalCoverThumbBlobKey} alt="" />
            ) : (
              <RoomIcon size={22} strokeWidth={2.2} />
            )}
          </span>
          <span className="room-card__name">{room.name}</span>
        </span>
        <span className="room-card__status-line">
          <span className="room-card__badge">{status.label}</span>
          <span className="room-card__count">{status.issueLabel}</span>
        </span>
        {status.totalCount > 0 && (
          <span className="room-card__progress" aria-hidden="true">
            <span className="room-card__progress-fill" style={{ width: `${status.progressPercent}%` }} />
          </span>
        )}
        <span className="room-card__detail">
          <span>{status.detail}</span>
          <ChevronRight size={17} aria-hidden="true" />
        </span>
      </button>
      <div className="room-card__actions" data-swipe-ignore="true">
        <button className="icon-button" type="button" aria-label={`Rename ${room.name}`} onClick={onRename}>
          <Edit3 size={16} aria-hidden="true" />
        </button>
        <button className="icon-button" type="button" aria-label={`Delete ${room.name}`} onClick={onDelete}>
          <Trash2 size={16} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

const ROOM_ICON_RULES: Array<{ terms: readonly string[]; icon: LucideIcon }> = [
  { terms: ["kitchen", "dining"], icon: Utensils },
  { terms: ["bath", "powder"], icon: Bath },
  { terms: ["bed"], icon: BedDouble },
  { terms: ["living", "family", "den", "office"], icon: Armchair },
  { terms: ["garage", "carport"], icon: CarFront },
  { terms: ["laundry"], icon: WashingMachine },
  { terms: ["entry", "foyer", "hall", "stair"], icon: DoorOpen },
  { terms: ["yard", "patio", "balcony", "outdoor"], icon: Trees },
  { terms: ["closet", "storage", "pantry", "mudroom"], icon: Boxes },
  { terms: ["basement", "attic", "utility", "mechanical"], icon: Warehouse },
  { terms: ["exterior", "front"], icon: House }
];

function getRoomIcon(name: string): LucideIcon {
  const normalized = name.toLowerCase();
  return ROOM_ICON_RULES.find((rule) => rule.terms.some((term) => normalized.includes(term)))?.icon ?? House;
}
